// Assignment form validation utilities

export const validateAssignmentForm = (formData) => {
  const errors = [];
  const warnings = [];

  // Required field validations
  const requiredFields = [
    { field: 'assetId', label: 'Asset' },
    { field: 'assignedToId', label: 'Employee' },
    { field: 'department', label: 'Department' },
    { field: 'assignedDate', label: 'Assignment Date' },
    { field: 'location', label: 'Location' },
    { field: 'purpose', label: 'Purpose' }
  ];

  requiredFields.forEach(({ field, label }) => {
    if (!formData[field] || formData[field].toString().trim() === '') {
      errors.push({
        field,
        message: `${label} is required`,
        type: 'required'
      });
    }
  });

  // Date validations
  if (formData.assignedDate) {
    const assignedDate = new Date(formData.assignedDate);
    if (isNaN(assignedDate.getTime())) {
      errors.push({
        field: 'assignedDate',
        message: 'Invalid assignment date format',
        type: 'format'
      });
    } else {
      // Check if assignment date is not in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (assignedDate > today) {
        warnings.push({
          field: 'assignedDate',
          message: 'Assignment date is in the future',
          type: 'warning'
        });
      }
    }
  }

  if (formData.expectedReturnDate) {
    const returnDate = new Date(formData.expectedReturnDate);
    if (isNaN(returnDate.getTime())) {
      errors.push({
        field: 'expectedReturnDate',
        message: 'Invalid expected return date format',
        type: 'format'
      });
    } else if (formData.assignedDate) {
      const assignedDate = new Date(formData.assignedDate);
      if (returnDate <= assignedDate) {
        errors.push({
          field: 'expectedReturnDate',
          message: 'Expected return date must be after assignment date',
          type: 'logic'
        });
      }
      
      // Check if return date is too far in the future
      const maxReturnDate = new Date(assignedDate);
      maxReturnDate.setFullYear(maxReturnDate.getFullYear() + 1);
      if (returnDate > maxReturnDate) {
        warnings.push({
          field: 'expectedReturnDate',
          message: 'Expected return date is more than 1 year from assignment date',
          type: 'warning'
        });
      }
    }
  }

  // Email validation
  if (formData.assignedToEmail) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.assignedToEmail)) {
      errors.push({
        field: 'assignedToEmail',
        message: 'Invalid email format',
        type: 'format'
      });
    }
  }

  // Phone validation
  if (formData.assignedToPhone) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.assignedToPhone.replace(/[\s\-\(\)]/g, ''))) {
      warnings.push({
        field: 'assignedToPhone',
        message: 'Phone number format may be invalid',
        type: 'warning'
      });
    }
  }

  // Text length validations
  if (formData.purpose && formData.purpose.length > 200) {
    errors.push({
      field: 'purpose',
      message: 'Purpose must be less than 200 characters',
      type: 'length'
    });
  }

  if (formData.notes && formData.notes.length > 500) {
    errors.push({
      field: 'notes',
      message: 'Notes must be less than 500 characters',
      type: 'length'
    });
  }

  if (formData.location && formData.location.length > 100) {
    errors.push({
      field: 'location',
      message: 'Location must be less than 100 characters',
      type: 'length'
    });
  }

  // Business logic validations
  if (formData.condition && !['Excellent', 'Good', 'Fair', 'Poor'].includes(formData.condition)) {
    errors.push({
      field: 'condition',
      message: 'Invalid condition value',
      type: 'enum'
    });
  }

  if (formData.status && !['Active', 'Returned', 'Pending', 'Overdue'].includes(formData.status)) {
    errors.push({
      field: 'status',
      message: 'Invalid status value',
      type: 'enum'
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// Real-time validation for individual fields
export const validateField = (field, value, formData = {}) => {
  const errors = [];
  const warnings = [];

  switch (field) {
    case 'assetId':
      if (!value || value.trim() === '') {
        errors.push('Asset selection is required');
      }
      break;

    case 'assignedToId':
      if (!value || value.trim() === '') {
        errors.push('Employee selection is required');
      }
      break;

    case 'department':
      if (!value || value.trim() === '') {
        errors.push('Department selection is required');
      }
      break;

    case 'assignedDate':
      if (!value) {
        errors.push('Assignment date is required');
      } else {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          errors.push('Invalid date format');
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date > today) {
            warnings.push('Assignment date is in the future');
          }
        }
      }
      break;

    case 'expectedReturnDate':
      if (value) {
        const returnDate = new Date(value);
        if (isNaN(returnDate.getTime())) {
          errors.push('Invalid date format');
        } else if (formData.assignedDate) {
          const assignedDate = new Date(formData.assignedDate);
          if (returnDate <= assignedDate) {
            errors.push('Return date must be after assignment date');
          }
        }
      }
      break;

    case 'assignedToEmail':
      if (value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors.push('Invalid email format');
        }
      }
      break;

    case 'purpose':
      if (value && value.length > 200) {
        errors.push('Purpose must be less than 200 characters');
      }
      break;

    case 'notes':
      if (value && value.length > 500) {
        errors.push('Notes must be less than 500 characters');
      }
      break;

    case 'location':
      if (!value || value.trim() === '') {
        errors.push('Location is required');
      } else if (value.length > 100) {
        errors.push('Location must be less than 100 characters');
      }
      break;

    default:
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// Format validation messages for display
export const formatValidationMessages = (validation) => {
  const { errors, warnings } = validation;
  const messages = [];

  if (errors.length > 0) {
    messages.push({
      type: 'error',
      title: 'Validation Errors',
      items: errors.map(error => 
        typeof error === 'string' ? error : error.message
      )
    });
  }

  if (warnings.length > 0) {
    messages.push({
      type: 'warning',
      title: 'Warnings',
      items: warnings.map(warning => 
        typeof warning === 'string' ? warning : warning.message
      )
    });
  }

  return messages;
};

// Get field-specific validation class
export const getValidationClass = (field, formData, touchedFields = {}) => {
  if (!touchedFields[field]) return '';
  
  const validation = validateField(field, formData[field], formData);
  return validation.isValid ? 'is-valid' : 'is-invalid';
};

// Sanitize form data
export const sanitizeFormData = (formData) => {
  const sanitized = { ...formData };

  // Trim string values
  Object.keys(sanitized).forEach(key => {
    if (typeof sanitized[key] === 'string') {
      sanitized[key] = sanitized[key].trim();
    }
  });

  // Remove empty strings for optional fields
  const optionalFields = ['notes', 'assignedToPhone', 'expectedReturnDate'];
  optionalFields.forEach(field => {
    if (sanitized[field] === '') {
      delete sanitized[field];
    }
  });

  return sanitized;
};
